import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { from, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { AuthModel, IUserRoleModel } from 'src/app/core/models/auth.model';
import { UsersStorageService } from 'src/app/core/storage/users-storage.service';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorService } from 'src/app/services/error.service';
import { environment } from 'src/environments/environment';
import * as AuthActions from '../actions/auth.actions';
import { AppState } from '../app.reducer';
import { selectAuthLoading } from '../selectors/auth.selector';

const handleAuthentication = async (
  usersStorageService: UsersStorageService,
  user: AuthModel
): Promise<Action<AuthActions.AuthActionTypes.AuthenticateSuccess>> => {
  const userFound = await usersStorageService.getUser(user.id);
  if (userFound) {
    await usersStorageService.saveUser(new AuthModel(user), 'update');
  } else {
    await usersStorageService.saveUser(new AuthModel(user), 'create');
  }
  const currentUser = await usersStorageService.getUser(user.id);

  return AuthActions.authenticateSuccess({ payload: { user: currentUser } });
};

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    public http: HttpClient,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private usersStorageService: UsersStorageService,
    private router: Router,
    private authService: AuthService
  ) { }

  authSignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.signupStart),
      switchMap(({ payload }) => {
        this.store.dispatch(
          AuthActions.authLoading({ payload: { loading: true } })
        );
        return this.http
          .post<IResponse<AuthModel>>(
            environment.apiUrl + '/auth/signup',
            { data: this.encodeString(JSON.stringify(payload)) }
          )
          .pipe(
            switchMap(() => {
              return of(
                AuthActions.signUpCompleted()
              );
            }),
            catchError((error) => {
              return of(
                AuthActions.authFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  authLogIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginStart),
      switchMap(({ payload }) => {
        this.store.dispatch(
          AuthActions.authLoading({ payload: { loading: true } })
        );
        return this.http
          .post<IResponse<AuthModel>>(
            environment.apiUrl + '/auth/login',
            { data: this.encodeString(JSON.stringify(payload)) }
          )
          .pipe(
            mergeMap((responseData) => {
              return handleAuthentication(
                this.usersStorageService,
                responseData.data
              );
            }),
            catchError((error) => {
              return of(
                AuthActions.authFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  refreshToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      switchMap((action) => {
        return this.http
          .post<
            IResponse<{
              access_token: string;
              refresh_token: string;
            }>
          >(environment.apiUrl + '/auth/refresh-token', action.payload)
          .pipe(
            mergeMap(async (responsePayload) => {
              await this.usersStorageService.updateUser(
                action.payload.user_id,
                responsePayload.data
              );

              return AuthActions.tokenRefreshed({
                payload: {
                  ...responsePayload.data,
                  user_id: action.payload.user_id,
                },
              });
            }),
            catchError((error) => {
              return of(
                AuthActions.authFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logoutUser),
        tap(async ({ payload }) => {
          await this.usersStorageService.deleteActiveUser(payload.id);
          void this.router.navigateByUrl('/auth');
        })
      );
    },
    { dispatch: false }
  );


  authenticateSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(AuthActions.authenticateSuccess),
      tap(() =>
        this.store.dispatch(AuthActions.authLoading({ payload: { loading: false } }))
      ),
      mergeMap(() =>
        this.store.select(selectAuthLoading).pipe(
          filter(loading => loading === false),
          take(1),

        )
      ),
      tap(() => {
        const firstpage = localStorage.getItem('firstpage');
        if (firstpage === '2') {
          this.router.navigateByUrl('/e9');
        } else if (firstpage === '3') {
          this.router.navigateByUrl('/dashboard');
        } else if (firstpage === '4') {
          this.router.navigateByUrl('/catalog');
        } else if (firstpage === '5') {
          this.router.navigateByUrl('/parcels');
        } else {
          this.router.navigateByUrl('/home');
        }
      })
    ),
    { dispatch: false }
  );



   downloadUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.downloadUserPermissions),
      switchMap(() =>
        this.http
          .get<IResponse<IUserRoleModel>>(
            `${environment.apiUrl}/auth/user-permissions`
          )
          .pipe(
            switchMap((response) => {
              const userId = this.authService.user.id;
              return from(
                this.usersStorageService.updateUserPermissions(
                  userId,
                  response.data
                )
              ).pipe(
                mergeMap(() => [
                  AuthActions.userPermissionsDownloaded({
                    payload: { userId, role: response.data },
                  }),
                  AuthActions.userPermissionsDownloadSuccessReport(),
                ])
              );
            }),
            startWith(
              AuthActions.initializeUserPermissionsDownloadReport()
            ),
            catchError((error) =>
              of(
                AuthActions.authFail({
                  payload: { error: this.errorService.mapError(error) },
                }),
                AuthActions.userPermissionsDownloadErrorReport()
              )
            )
          )
      )
    )
  );

  getConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getTheConfig),
      switchMap(() => {
        return this.http
          .get<{ logoutTimeout: number }>(environment.apiUrl + '/auth/config')
          .pipe(
            map((config) => {
              return AuthActions.setConfig({ payload: config });
            }),
            catchError((error) => {
              return of(
                AuthActions.authFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  encodeString(str) {
    let strEncoded = str;
    const safetyLoops = 12;
    for (let i = 0; i < safetyLoops; i++) {
      strEncoded = `${strEncoded.substring(Math.ceil(strEncoded.length / 2),
         strEncoded.length)}${strEncoded.substring(0, Math.ceil(strEncoded.length / 2))}`;
      strEncoded = i > 0 ? `${strEncoded.substring(0, 10)}${this.getRandomChar(6)}${strEncoded.substring(10,
         strEncoded.length)}${this.getRandomChar(6)}` :
        `${strEncoded}${this.getRandomChar(12)}`;
      strEncoded = btoa(encodeURIComponent(`${strEncoded}`));
    }
    return strEncoded;
  }

  getRandomChar(length) {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

}
